import { useMemo, useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TestRunEditDialog } from '@/features/executions/components/TestRunEditDialog';
import { useTestRunEditDialog } from '@/features/executions/components/useTestRunEditDialog';
import { useTestRunsTableRowActions } from '@/features/executions/components/useTestRunsTableRowActions';
import { EditFeatureDialog } from '@/features/features/components/dialogs';
import {
  FeatureTestRunsNestedTable,
  type FeatureTestRunActions,
} from '@/features/features/components/FeatureTestRunsNestedTable';
import { useFeatureEditDialog } from '@/features/features/components/useFeatureEditDialog';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { tableV2NestedCarrierRowSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2PassRateCell,
  TableV2ProjectCell,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { buildTagColorByName } from '@/features/shared/tags';
import { featureStatusOptions } from '@/shared/constants/statusOptions';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import { useFeaturesTableRowActions } from './useFeaturesTableRowActions';
import type { FeatureDeliveryMode } from '@/features/features/pages/featuresTableDefinition';
import type { TableV2SkeletonColumnKind } from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { FeatureDto, ProjectDto, QueryResult } from '@flowqa/shared';
import type { QueryKey } from '@tanstack/react-query';

type FeaturesTableV2Props = {
  active?: boolean;
  featureMode?: FeatureTableDateMode;
  fixedFilters?: GridQueryInput['filters'];
  forceLoading?: boolean;
  initialPageSize?: number;
  noRowsText: string;
  pageSizeOptions?: number[];
  projectOptions?: Array<
    Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'id' | 'name'>
  >;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<FeatureDto>>;
  queryKey: QueryKey;
  requireAuth?: boolean;
  search?: string;
  selectedProjectId: string | null;
  sort: ApiSort;
  tagOptionsOverride?: TagOption[];
  onSortChange: (sort: ApiSort) => void;
};

type SortableFeatureField = 'name' | 'plannedFor' | 'releasedAt' | 'status';
type FeatureTableDateMode = FeatureDeliveryMode | 'all';
type FeatureColumnWidths = {
  name: string;
  status: string;
  firstDate: string;
  testStatus: string;
  tags: string;
};

const featureSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'dateTime',
  'dateTime',
  'passRate',
  'tags',
  'actions',
] as const;

const resolveFeatureColumnWidths = (
  showProjectColumn: boolean,
  featureMode: FeatureTableDateMode,
): FeatureColumnWidths => {
  if (featureMode === 'all') {
    return {
      name: showProjectColumn ? '19%' : '21%',
      status: showProjectColumn ? '10%' : '11%',
      firstDate: showProjectColumn ? '12%' : '13%',
      testStatus: showProjectColumn ? '18%' : '20%',
      tags: showProjectColumn ? '12%' : '13%',
    };
  }

  return {
    name: showProjectColumn ? '22%' : '24%',
    status: showProjectColumn ? '11%' : '12%',
    firstDate: showProjectColumn ? '13%' : '14%',
    testStatus: showProjectColumn ? '20%' : '22%',
    tags: showProjectColumn ? '14%' : '16%',
  };
};

const buildFeatureColgroup = (
  showProjectColumn: boolean,
  featureMode: FeatureTableDateMode,
): ReactElement => {
  const widths = resolveFeatureColumnWidths(showProjectColumn, featureMode);

  return (
    <colgroup>
      <col width={tableV2ChevronColumnWidth} />
      <col width={widths.name} />
      <col width={widths.status} />
      <col width={widths.firstDate} />
      {featureMode === 'all' ? <col width={widths.firstDate} /> : null}
      <col width={widths.testStatus} />
      <col width={widths.tags} />
      {showProjectColumn ? <col width="10%" /> : null}
      <col width={tableV2ActionsColumnWidth} />
    </colgroup>
  );
};

const buildFeatureSkeletonColumns = (
  showProjectColumn: boolean,
  featureMode: FeatureTableDateMode,
): TableV2SkeletonColumnKind[] => [
  ...featureSkeletonColumns.slice(0, featureMode === 'all' ? -1 : 4),
  ...(featureMode === 'all' ? [] : featureSkeletonColumns.slice(5, -1)),
  ...(showProjectColumn ? (['project'] as const) : []),
  featureSkeletonColumns[featureSkeletonColumns.length - 1],
];

const FeatureRow = ({
  deleteRowPendingId,
  isLastVisibleRow,
  row,
  rowProject,
  featureMode,
  showProjectColumn,
  tagOptions,
  testRunActions,
  updateRowTagsColorPendingId,
  onDeleteRow,
  onEditRow,
  onUpdateRowTagsColor,
}: {
  deleteRowPendingId: string | null;
  isLastVisibleRow: boolean;
  row: FeatureDto;
  rowProject: Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'name'> | null;
  featureMode: FeatureTableDateMode;
  showProjectColumn: boolean;
  tagOptions: TagOption[];
  testRunActions: FeatureTestRunActions;
  updateRowTagsColorPendingId: string | null;
  onDeleteRow: (row: FeatureDto) => void;
  onEditRow: (row: FeatureDto) => void;
  onUpdateRowTagsColor: (input: { row: FeatureDto; tags: string[]; colors: TagColor[] }) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const tagColorByName = buildTagColorByName(tagOptions);
  const testCounts = {
    total:
      (row.testLatestRunPassed ?? 0) +
      (row.testLatestRunFailed ?? 0) +
      (row.testLatestRunErrored ?? 0) +
      (row.testLatestRunSkipped ?? 0) +
      (row.testLatestRunUntested ?? 0),
    passed: row.testLatestRunPassed ?? 0,
    failed: row.testLatestRunFailed ?? 0,
    errored: row.testLatestRunErrored ?? 0,
    skipped: row.testLatestRunSkipped ?? 0,
    untested: row.testLatestRunUntested ?? 0,
  };

  return (
    <>
      <TableRow
        sx={buildTableV2BodyRowSurfaceSx({ hasRenderedDivider: !expanded && !isLastVisibleRow })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse feature test runs' : 'Expand feature test runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={`/features/${row.slug}`}
            primary={row.name}
            secondary={row.releaseName ?? null}
            secondaryHref={row.releaseSlug ? `/releases/${row.releaseSlug}` : null}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        {featureMode !== 'released' ? (
          <TableCell>
            <TableV2DateCell value={row.plannedFor ?? null} />
          </TableCell>
        ) : null}
        {featureMode !== 'inProgress' ? (
          <TableCell>
            <TableV2DateCell value={row.releasedAt ?? null} />
          </TableCell>
        ) : null}
        <TableCell>
          <TableV2PassRateCell counts={testCounts} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showProjectColumn ? (
          <TableCell>
            <TableV2ProjectCell project={rowProject} fallbackName={row.projectName} />
          </TableCell>
        ) : null}
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={row.name}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Feature')}
            editTagsEntityNoun="feature"
            rowTags={row.tags ?? []}
            onEdit={() => {
              onEditRow(row);
            }}
            onUpdateTagsColor={(input) => {
              onUpdateRowTagsColor({ row, ...input });
            }}
            updateTagsColorLoading={updateRowTagsColorPendingId === row.id}
            deleteActionLabel={getEntityActionLabel('delete', 'Feature')}
            onDelete={() => {
              onDeleteRow(row);
            }}
            disableDelete={deleteRowPendingId === row.id}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? {
                '& > td': {
                  borderBottom: (theme) => `1px solid ${theme.app.colors.border}`,
                },
              }
            : {},
        ]}
      >
        <TableCell colSpan={buildFeatureSkeletonColumns(showProjectColumn, featureMode).length}>
          <FeatureTestRunsNestedTable
            childCount={testCounts.total}
            expanded={expanded}
            featureId={row.id}
            projectId={row.projectId}
            tagColorByName={tagColorByName}
            testRunActions={testRunActions}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const FeaturesTableV2 = ({
  active = true,
  featureMode = 'all',
  fixedFilters,
  forceLoading,
  initialPageSize,
  noRowsText,
  pageSizeOptions,
  projectOptions = [],
  queryFn,
  queryKey,
  requireAuth = true,
  search,
  selectedProjectId,
  sort,
  tagOptionsOverride = [],
  onSortChange,
}: FeaturesTableV2Props): ReactElement => {
  const showProjectColumn = selectedProjectId === null;
  const skeletonColumns = buildFeatureSkeletonColumns(showProjectColumn, featureMode);
  const projectById = useMemo(
    () => new Map(projectOptions.map((project) => [project.id, project])),
    [projectOptions],
  );
  const {
    editErrors,
    editForm,
    editReleaseOptions,
    editSaving,
    editTarget,
    onEditClose,
    onEditRow,
    onEditSave,
  } = useFeatureEditDialog();
  const {
    editLoading: testRunEditLoading,
    editTarget: testRunEditTarget,
    onEditClose: onTestRunEditClose,
    onEditRow: onEditTestRunRow,
    onEditSave: onTestRunEditSave,
  } = useTestRunEditDialog(selectedProjectId);
  const {
    deleteLoading,
    deleteRowPendingId,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId,
  } = useFeaturesTableRowActions({ selectedProjectId });
  const testRunRowActions = useTestRunsTableRowActions({ selectedProjectId });
  const testRunActions: FeatureTestRunActions = {
    ...testRunRowActions,
    onEditRow: onEditTestRunRow,
  };

  const handleSortChange = (field: SortableFeatureField): void => {
    onSortChange({
      field,
      direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc',
    });
  };

  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="name"
          label="Feature"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="status"
          label="Status"
          onSortChange={handleSortChange}
        />
      </TableCell>
      {featureMode !== 'released' ? (
        <TableCell>
          <TableV2SortableHeader
            activeSort={sort}
            field="plannedFor"
            label="Planned For"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {featureMode !== 'inProgress' ? (
        <TableCell>
          <TableV2SortableHeader
            activeSort={sort}
            field="releasedAt"
            label="Released At"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      <TableCell>
        <TableV2HeaderLabel label="Testing Status" />
      </TableCell>
      <TableCell>
        <TableV2HeaderLabel label="Tags" />
      </TableCell>
      {showProjectColumn ? (
        <TableCell>
          <TableV2HeaderLabel label="Project" />
        </TableCell>
      ) : null}
      <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Features"
        colgroup={buildFeatureColgroup(showProjectColumn, featureMode)}
        emptyText={noRowsText}
        entityLabel="features"
        fixedFilters={fixedFilters}
        forceLoading={forceLoading}
        header={header}
        initialPageSize={initialPageSize}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        requireAuth={requireAuth}
        search={search}
        skeletonColumns={skeletonColumns}
        sorts={[sort]}
        renderRow={(row, context) => (
          <FeatureRow
            key={row.id}
            deleteRowPendingId={deleteRowPendingId}
            isLastVisibleRow={context.isLastVisibleRow}
            row={row}
            rowProject={projectById.get(row.projectId) ?? null}
            featureMode={featureMode}
            showProjectColumn={showProjectColumn}
            tagOptions={tagOptionsOverride}
            testRunActions={testRunActions}
            updateRowTagsColorPendingId={updateRowTagsColorPendingId}
            onDeleteRow={onDeleteRow}
            onEditRow={onEditRow}
            onUpdateRowTagsColor={onUpdateRowTagsColor}
          />
        )}
      />
      <EditFeatureDialog
        open={editTarget !== null}
        saving={editSaving}
        values={editForm.values}
        errors={{ name: editErrors.fieldErrorProps('name') }}
        statusOptions={featureStatusOptions}
        releaseOptions={editReleaseOptions}
        errorMessage={editForm.submitError}
        onClose={onEditClose}
        onSave={onEditSave}
        handlers={{
          onNameChange: (value) => {
            editForm.setField('name', value);
          },
          onStatusChange: (value) => {
            editForm.setField('status', value);
          },
          onReleaseChange: (value) => {
            editForm.setField('releaseId', value);
          },
          onPlannedForChange: (value) => {
            editForm.setField('plannedFor', value);
          },
          onReleasedAtChange: (value) => {
            editForm.setField('releasedAt', value);
          },
          onTagsChange: (value) => {
            editForm.setField('tags', value);
          },
        }}
      />
      <TestRunEditDialog
        open={testRunEditTarget !== null}
        execution={testRunEditTarget}
        loading={testRunEditLoading}
        onClose={onTestRunEditClose}
        onSave={onTestRunEditSave}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'feature' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'feature',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'feature',
        })}
        confirmColor="error"
        loading={deleteLoading}
        onClose={onDeleteClose}
        onConfirm={onDeleteConfirm}
      />
      <ConfirmDialog
        open={testRunRowActions.deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'testRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'testRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'testRun',
        })}
        confirmColor="error"
        loading={testRunRowActions.deleteLoading}
        onClose={testRunRowActions.onDeleteClose}
        onConfirm={testRunRowActions.onDeleteConfirm}
      />
    </>
  );
};
